import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AtSign, Loader2, X, ArrowRight, Check, Music2, Instagram, Youtube, Facebook } from "lucide-react";

const NICHES = ["Comedy", "Dance", "Beauty", "Fitness", "Food", "Gaming", "Fashion", "Education", "Music", "Travel", "Tech", "Lifestyle"];
const AGES = ["13-17", "18-24", "25-34", "35-44", "45+"];
const LANGS = ["English", "Spanish", "French", "German", "Portuguese", "Hindi", "Arabic", "Japanese"];
const LOCATIONS = ["United States", "United Kingdom", "Canada", "Australia", "Global", "Europe", "Latin America", "Asia"];
const PLATFORM_META = [
  { id: "tiktok", name: "TikTok", icon: Music2 },
  { id: "instagram", name: "Instagram", icon: Instagram },
  { id: "youtube", name: "YouTube", icon: Youtube },
  { id: "facebook", name: "Facebook", icon: Facebook },
];

const Onboarding = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [pf, setPf] = useState({
    tiktok: { on: true, handle: "", followers: "" },
    instagram: { on: false, handle: "", followers: "" },
    youtube: { on: false, handle: "", followers: "" },
    facebook: { on: false, handle: "", followers: "" },
  });
  const [niche, setNiche] = useState("");
  const [interests, setInterests] = useState([]);
  const [interestInput, setInterestInput] = useState("");
  const [age, setAge] = useState("18-24");
  const [location, setLocation] = useState("United States");
  const [language, setLanguage] = useState("English");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.onboarded) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const addInterest = (val) => {
    const v = val.trim();
    if (v && !interests.includes(v) && interests.length < 8) setInterests([...interests, v]);
    setInterestInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const platforms = PLATFORM_META
      .filter((p) => pf[p.id].on && pf[p.id].handle.trim())
      .map((p) => ({ platform: p.id, handle: pf[p.id].handle, starting_followers: parseInt(pf[p.id].followers || "0", 10) }));
    if (!platforms.length) { toast.error("Add at least one platform handle"); return; }
    if (!niche) { toast.error("Please choose a niche"); return; }
    setLoading(true);
    try {
      await api.post("/onboarding", {
        platforms,
        target_audience: { niche, interests, age_range: age, location, language },
      });
      await refreshUser();
      toast.success("Profile saved! Pick a plan to launch your campaign.");
      navigate("/plans", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <div className="absolute top-0 left-1/3 w-[400px] h-[400px] bg-primary/15 rounded-full blur-[130px] pointer-events-none" />
      <div className="relative max-w-2xl mx-auto px-6 py-10">
        <Logo />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Step 1 of 2 · Setup</p>
          <h1 className="font-heading text-4xl font-black tracking-tight">Tell us who you want to reach</h1>
          <p className="text-muted-foreground mt-3">We use this to target real viewers most likely to follow and engage. We never ask for your password.</p>

          <form onSubmit={submit} className="mt-8 space-y-6 rounded-2xl border border-border/50 bg-card p-7">
            <div>
              <Label className="text-sm">Your platforms <span className="text-muted-foreground font-normal">(select and add handles)</span></Label>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                {PLATFORM_META.map((p) => {
                  const st = pf[p.id];
                  return (
                    <div key={p.id} className={`rounded-xl border p-4 transition-colors ${st.on ? "border-primary/40 bg-primary/5" : "border-white/10"}`}>
                      <button type="button" onClick={() => setPf({ ...pf, [p.id]: { ...st, on: !st.on } })}
                        className="w-full flex items-center justify-between" data-testid={`platform-toggle-${p.id}`}>
                        <span className="flex items-center gap-2 font-medium"><p.icon className="w-4 h-4" /> {p.name}</span>
                        <span className={`w-5 h-5 rounded-md border flex items-center justify-center ${st.on ? "bg-primary border-primary" : "border-white/20"}`}>
                          {st.on && <Check className="w-3.5 h-3.5 text-background" />}
                        </span>
                      </button>
                      {st.on && (
                        <div className="mt-3 space-y-2">
                          <div className="relative">
                            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            <Input value={st.handle} onChange={(e) => setPf({ ...pf, [p.id]: { ...st, handle: e.target.value } })} placeholder="handle"
                              className="pl-8 h-10 bg-transparent border-white/10 focus:border-primary" data-testid={`platform-handle-${p.id}`} />
                          </div>
                          <Input type="number" min="0" value={st.followers} onChange={(e) => setPf({ ...pf, [p.id]: { ...st, followers: e.target.value } })} placeholder="current followers"
                            className="h-10 bg-transparent border-white/10 focus:border-primary" data-testid={`platform-followers-${p.id}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-sm">Primary niche</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {NICHES.map((n) => (
                  <button type="button" key={n} onClick={() => setNiche(n)} data-testid={`niche-${n.toLowerCase()}`}
                    className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${niche === n ? "bg-primary text-background border-primary" : "border-white/15 text-muted-foreground hover:border-white/40"}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm">Interests / hashtags <span className="text-muted-foreground font-normal">(press Enter)</span></Label>
              <Input value={interestInput} onChange={(e) => setInterestInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addInterest(interestInput); } }}
                placeholder="e.g. skincare, grwm, routine" className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" data-testid="onboard-interests-input" />
              {interests.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {interests.map((it) => (
                    <Badge key={it} variant="secondary" className="bg-white/5 border border-white/10 gap-1.5 py-1">
                      {it}<button type="button" onClick={() => setInterests(interests.filter((x) => x !== it))}><X className="w-3 h-3" /></button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
              <div>
                <Label className="text-sm">Age range</Label>
                <Select value={age} onValueChange={setAge}>
                  <SelectTrigger className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="onboard-age-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{AGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Location</Label>
                <Select value={location} onValueChange={setLocation}>
                  <SelectTrigger className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="onboard-location-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="onboard-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-primary text-background font-bold hover:bg-primary/90 transition-colors" data-testid="onboard-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue to plans <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default Onboarding;
